/**
 * StockEntryValueSummarySection — bespoke Thai-SME-friendly value summary
 * card. Driven by STOCK_ACTIONS.valueDisplay so the presentation matches
 * the nature of the stock movement:
 *
 *   - "incoming" (Material Receipt)     → green incoming-only card
 *   - "outgoing" (Material Issue)       → red outgoing-only card
 *   - "transfer" (Transfer / Manufacture / Repack) → outgoing + incoming + difference
 *
 * Read-only display. Editing these totals is not user-driven — ERPNext
 * computes them from `basic_amount` on each row.
 *
 * Design language matches the sibling Overview/Details cards:
 *   - uppercase pl-4 section header
 *   - CARD token from design-tokens.ts
 *   - flex items-center justify-between rows
 */

import { ArrowDownCircle, ArrowUpCircle, Check, Scale } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/i18n/useLanguage";
import {
  STOCK_ACTIONS,
  type ValueDisplayMode,
} from "@/features/common/doc-dashboard/stockActionsConfig";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";

const i18n: Record<string, { en: string; th: string }> = {
  value_summary: { en: "Value summary", th: "สรุปมูลค่า" },
  outgoing_value: { en: "Outgoing value", th: "มูลค่าออก" },
  incoming_value: { en: "Incoming value", th: "มูลค่าเข้า" },
  value_difference: { en: "Difference", th: "ส่วนต่าง" },
  balanced: { en: "Balanced", th: "ยอดสมดุล" },
  unbalanced: { en: "Unbalanced", th: "ยอดไม่สมดุล" },
  no_values: { en: "No values yet", th: "ยังไม่มีมูลค่า" },
  no_values_hint: {
    en: "Add items and basic rates to see the total value.",
    th: "เพิ่มสินค้าและราคาเพื่อดูมูลค่ารวม",
  },
};

function formatCurrency(value: number | undefined, currency: string): string {
  const v = Number(value) || 0;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "THB",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);
  } catch {
    return `${v.toFixed(2)} ${currency || "THB"}`;
  }
}

function getValueDisplay(stockEntryType: string | undefined): ValueDisplayMode | undefined {
  if (!stockEntryType) return undefined;
  const action = STOCK_ACTIONS.find(
    (a) => a.kind === "stock_entry_quick_action" && a.stockEntryType === stockEntryType,
  );
  return action?.valueDisplay;
}

interface ValueRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: "incoming" | "outgoing" | "neutral";
  isFirst?: boolean;
}

function ValueRow({ icon, label, value, tone, isFirst }: ValueRowProps) {
  const toneClass =
    tone === "incoming"
      ? "text-emerald-700 dark:text-emerald-300"
      : tone === "outgoing"
        ? "text-rose-700 dark:text-rose-300"
        : "text-foreground";
  return (
    <div
      className={`flex items-center justify-between gap-3 py-3 ${
        isFirst ? "" : "border-t border-border/40"
      }`}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground min-w-0">
        <span className="shrink-0">{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <div className={`text-sm font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}

export function StockEntryValueSummarySection({ formData }: ShopifySectionWidgetProps) {
  const { language } = useLanguage();
  const t = (key: string) => {
    const entry = i18n[key];
    return entry ? (language === "th" ? entry.th : entry.en) : key;
  };

  const stockEntryType = (formData.stock_entry_type as string) || "";
  const currency = (formData.currency as string) || "THB";
  const outgoing = Number(formData.total_outgoing_value) || 0;
  const incoming = Number(formData.total_incoming_value) || 0;
  const rawDifference = formData.value_difference;
  const difference =
    rawDifference !== undefined && rawDifference !== null && !Number.isNaN(Number(rawDifference))
      ? Number(rawDifference)
      : incoming - outgoing;

  const mode = getValueDisplay(stockEntryType);
  const hasAnyValue = outgoing > 0 || incoming > 0;

  // No type picked or no values — render a clean empty state
  if (!mode || !hasAnyValue) {
    return (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
          {t("value_summary")}
        </h3>
        <Card className={CARD}>
          <CardContent className="p-5">
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-muted mb-3">
                <Scale className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-sm font-medium text-muted-foreground">
                {t("no_values")}
              </div>
              <div className="text-xs text-muted-foreground mt-1 max-w-xs">
                {t("no_values_hint")}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isBalanced = Math.abs(difference) < 0.005;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
        {t("value_summary")}
      </h3>
      <Card className={CARD}>
        <CardContent className="p-5">
          {mode === "incoming" ? (
            <ValueRow
              icon={<ArrowDownCircle className="h-4 w-4 text-emerald-600" />}
              label={t("incoming_value")}
              value={formatCurrency(incoming, currency)}
              tone="incoming"
              isFirst
            />
          ) : null}

          {mode === "outgoing" ? (
            <ValueRow
              icon={<ArrowUpCircle className="h-4 w-4 text-rose-600" />}
              label={t("outgoing_value")}
              value={formatCurrency(outgoing, currency)}
              tone="outgoing"
              isFirst
            />
          ) : null}

          {mode === "transfer" ? (
            <>
              <ValueRow
                icon={<ArrowUpCircle className="h-4 w-4 text-rose-600" />}
                label={t("outgoing_value")}
                value={formatCurrency(outgoing, currency)}
                tone="outgoing"
                isFirst
              />
              <ValueRow
                icon={<ArrowDownCircle className="h-4 w-4 text-emerald-600" />}
                label={t("incoming_value")}
                value={formatCurrency(incoming, currency)}
                tone="incoming"
              />
              <div className="flex items-center justify-between gap-3 pt-3 mt-2 border-t-2 border-border">
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground min-w-0">
                  <Scale className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{t("value_difference")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-sm font-semibold tabular-nums ${
                      isBalanced
                        ? "text-foreground"
                        : "text-amber-700 dark:text-amber-300"
                    }`}
                  >
                    {formatCurrency(difference, currency)}
                  </span>
                  {isBalanced ? (
                    <span className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
                      <Check className="h-3.5 w-3.5" />
                      <span>{t("balanced")}</span>
                    </span>
                  ) : (
                    <span className="text-xs text-amber-700 dark:text-amber-300">
                      {t("unbalanced")}
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
